import { useState, useRef, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';
import { useCoachThreads, useCoachThreadMessages, useCoachClientList, type CoachThread, type CoachMessage } from '@/hooks/useCoachThreads';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from '@/components/ui/drawer';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { ArrowLeft, Send, Check, Link2, Plus, Reply, Pencil, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { EmojiPickerButton } from './EmojiPickerButton';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { useToast } from '@/hooks/use-toast';
import { ChatQuotedPreview, type QuotedMessage } from './ChatQuotedPreview';
import { useThreadReactions } from '@/hooks/useMessageReactions';
import { MessageReactions } from './MessageReactions';
import { linkify } from '@/utils/linkify';

interface CoachChatPanelProps {
  open: boolean;
  onClose: () => void;
}

function CoachThreadListView({
  threads,
  onSelectThread,
  onNewMessage,
}: {
  threads: CoachThread[];
  onSelectThread: (thread: CoachThread) => void;
  onNewMessage: () => void;
}) {
  const formatWhen = (iso?: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    const sameDay = new Date().toDateString() === d.toDateString();
    return sameDay
      ? d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  return (
    <div className="flex flex-col h-full">
      <div className="p-2 space-y-1 flex-1 overflow-y-auto">
        {threads.length === 0 ? (
          <div className="text-center py-8 space-y-2">
            <p className="text-sm text-muted-foreground">No client messages yet.</p>
            <p className="text-xs text-muted-foreground">Start a conversation with a client below.</p>
          </div>
        ) : (
          threads.map(thread => (
            <button
              key={thread.id}
              onClick={() => onSelectThread(thread)}
              className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-secondary/60 transition-colors flex items-start gap-2"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className={cn('text-sm truncate', thread.needs_reply ? 'font-semibold text-foreground' : 'font-medium text-foreground')}>
                    {thread.client_name}
                  </p>
                  {thread.needs_reply && (
                    <Badge variant="destructive" className="text-[10px] h-4 px-1.5 shrink-0">Needs reply</Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground truncate">
                  {thread.last_message_text
                    ? `${thread.last_message_from_client ? '' : 'You: '}${thread.last_message_text}`
                    : 'No messages yet'}
                </p>
              </div>
              <span className="text-[10px] text-muted-foreground shrink-0 pt-0.5">
                {formatWhen(thread.last_message_at || thread.updated_at)}
              </span>
            </button>
          ))
        )}
      </div>
      <div className="p-4 border-t">
        <Button onClick={onNewMessage} className="w-full gap-2" size="sm">
          <Plus className="h-4 w-4" />
          Message a client
        </Button>
      </div>
    </div>
  );
}

function CoachNewMessageView({
  onBack,
  onPicked,
}: {
  onBack: () => void;
  onPicked: (clientId: string) => Promise<void> | void;
}) {
  const { clients, loading: clientsLoading } = useCoachClientList();
  const [selectedClientId, setSelectedClientId] = useState('');
  const [opening, setOpening] = useState(false);

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b">
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
      </div>
      <div className="flex-1 p-4 space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Open a conversation with</label>
          <Select value={selectedClientId} onValueChange={setSelectedClientId}>
            <SelectTrigger>
              <SelectValue placeholder={clientsLoading ? 'Loading…' : 'Select a client…'} />
            </SelectTrigger>
            <SelectContent>
              {clients.map(client => (
                <SelectItem key={client.id} value={client.id}>
                  {client.full_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Each client has one continuous conversation, so this opens their existing chat history.
          </p>
        </div>
      </div>
      <div className="p-4 border-t">
        <Button
          className="w-full"
          size="sm"
          disabled={!selectedClientId || opening}
          onClick={async () => {
            setOpening(true);
            await onPicked(selectedClientId);
            setOpening(false);
          }}
        >
          {opening ? 'Opening…' : 'Open conversation'}
        </Button>
      </div>
    </div>
  );
}

function CoachThreadDetailView({
  thread,
  onBack,
}: {
  thread: CoachThread;
  onBack: () => void;
}) {
  const { user } = useAuth();
  const { toast } = useToast();
  const { messages } = useCoachThreadMessages(thread.id);
  const [clientLastReadAt, setClientLastReadAt] = useState<string | null>(thread.client_last_read_at);

  useEffect(() => {
    setClientLastReadAt(thread.client_last_read_at);
  }, [thread.id, thread.client_last_read_at]);

  // Live updates when the client opens the chat and read marker advances
  useEffect(() => {
    const channel = supabase
      .channel(`coach-thread-read-${thread.id}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'client_threads', filter: `id=eq.${thread.id}` },
        (payload) => {
          const next = (payload.new as { client_last_read_at: string | null } | null)?.client_last_read_at ?? null;
          setClientLastReadAt(next);
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [thread.id]);

  // Index of the most recent coach message the client has seen
  const lastSeenCoachIdx = (() => {
    if (!clientLastReadAt) return -1;
    const readMs = new Date(clientLastReadAt).getTime();
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.sender_role === 'coach' && new Date(m.created_at).getTime() <= readMs) {
        return i;
      }
    }
    return -1;
  })();
  const [reply, setReply] = useState('');
  const [buttonLabel, setButtonLabel] = useState('');
  const [buttonRoute, setButtonRoute] = useState('');
  const [showActionLink, setShowActionLink] = useState(false);
  const [sending, setSending] = useState(false);
  const [replyingTo, setReplyingTo] = useState<QuotedMessage | null>(null);
  const [flashId, setFlashId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  const messageRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const messageIds = messages.map((m) => m.id);
  const { reactions, toggle: toggleReaction } = useThreadReactions(thread.id, messageIds);
  const reactionsByMessage = reactions.reduce<Record<string, typeof reactions>>((acc, r) => {
    (acc[r.message_id] ||= []).push(r);
    return acc;
  }, {});

  const clientFirstName = thread.client_name?.split(' ')[0] || 'Client';
  const senderLabelFor = (role: 'client' | 'coach') =>
    role === 'coach' ? 'You' : clientFirstName;

  const scrollToMessage = (id: string) => {
    const el = messageRefs.current[id];
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setFlashId(id);
    setTimeout(() => setFlashId(prev => (prev === id ? null : prev)), 1500);
  };

  const startReplyTo = (msg: CoachMessage) => {
    setReplyingTo({
      id: msg.id,
      sender_role: msg.sender_role,
      message_text: msg.message_text,
      voice_url: msg.voice_url,
    });
    textareaRef.current?.focus();
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  const EDIT_WINDOW_MS = 15 * 60 * 1000;
  const isOwn = (msg: CoachMessage) => msg.sender_role === 'coach' && msg.sender_id === user?.id;
  const canEdit = (msg: CoachMessage) =>
    isOwn(msg) && now - new Date(msg.created_at).getTime() < EDIT_WINDOW_MS;

  const startEdit = (msg: CoachMessage) => {
    setEditingId(msg.id);
    setEditingText(msg.message_text);
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditingText('');
  };
  const saveEdit = async () => {
    if (!editingId) return;
    const text = editingText.trim();
    if (!text) return;
    setSavingEdit(true);
    const { error } = await supabase
      .from('client_messages')
      .update({ message_text: text, edited_at: new Date().toISOString() } as any)
      .eq('id', editingId);
    setSavingEdit(false);
    if (error) {
      toast({ title: "Couldn't update message", description: error.message, variant: 'destructive' });
      return;
    }
    cancelEdit();
  };
  const confirmDelete = async () => {
    if (!deleteId) return;
    const id = deleteId;
    setDeleteId(null);
    const { error } = await supabase.from('client_messages').delete().eq('id', id);
    if (error) {
      toast({ title: "Couldn't delete message", description: error.message, variant: 'destructive' });
    }
  };

  const handleSendReply = async () => {
    if (!reply.trim() || !user) return;
    setSending(true);

    await supabase.from('client_messages').insert({
      thread_id: thread.id,
      sender_role: 'coach',
      sender_id: user.id,
      message_text: reply.trim(),
      button_label: buttonLabel.trim() || null,
      button_route: buttonRoute.trim() || null,
      reply_to_id: replyingTo?.id ?? null,
    } as any);

    setReply('');
    setButtonLabel('');
    setButtonRoute('');
    setShowActionLink(false);
    setReplyingTo(null);
    setSending(false);
    toast({ title: 'Reply sent' });
  };

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground line-clamp-1">{thread.client_name}</p>
            <p className="text-[11px] text-muted-foreground">Continuous conversation</p>
          </div>
        </div>
      </div>

      <ScrollArea className="flex-1 p-4">
        <div className="space-y-3">
          {messages.map((msg, idx) => (
            <div
              key={msg.id}
              ref={(el) => { messageRefs.current[msg.id] = el; }}
              className={cn(
                "group flex gap-2 transition-colors rounded-xl -mx-1 px-1 py-0.5",
                msg.sender_role === 'client' ? 'justify-start' : 'justify-end',
                flashId === msg.id && 'bg-primary/10',
              )}
            >
              {msg.sender_role === 'coach' && (
                <div className="self-center opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity flex items-center gap-1 shrink-0">
                  {canEdit(msg) && (
                    <button
                      type="button"
                      onClick={() => startEdit(msg)}
                      className="h-7 w-7 rounded-full bg-muted hover:bg-muted/80 text-muted-foreground flex items-center justify-center"
                      aria-label="Edit message"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {isOwn(msg) && (
                    <button
                      type="button"
                      onClick={() => setDeleteId(msg.id)}
                      className="h-7 w-7 rounded-full bg-muted hover:bg-destructive/10 text-muted-foreground hover:text-destructive flex items-center justify-center"
                      aria-label="Delete message"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => startReplyTo(msg)}
                    className="h-7 w-7 rounded-full bg-muted hover:bg-muted/80 text-muted-foreground flex items-center justify-center"
                    aria-label="Reply to message"
                  >
                    <Reply className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
              <div className="flex flex-col max-w-[85%] min-w-0">
              <div className={cn(
                "min-w-0 rounded-2xl px-4 py-2.5 text-sm break-words [overflow-wrap:anywhere]",
                msg.sender_role === 'client'
                  ? 'bg-secondary text-secondary-foreground rounded-bl-md self-start'
                  : 'bg-primary text-primary-foreground rounded-br-md self-end'
              )}>
                {(msg.reply_to_id || msg.reply_to) && (
                  <div className="mb-1.5">
                    <ChatQuotedPreview
                      message={msg.reply_to ?? null}
                      senderLabel={
                        msg.reply_to
                          ? senderLabelFor(msg.reply_to.sender_role)
                          : 'Message'
                      }
                      variant="bubble"
                      onPrimary={msg.sender_role === 'coach'}
                      onClick={msg.reply_to ? () => scrollToMessage(msg.reply_to!.id) : undefined}
                    />
                  </div>
                )}
                {editingId === msg.id ? (
                  <div className="space-y-2">
                    <Textarea
                      value={editingText}
                      onChange={(e) => setEditingText(e.target.value)}
                      className="min-h-[60px] text-sm bg-background text-foreground"
                      rows={2}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          void saveEdit();
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          cancelEdit();
                        }
                      }}
                    />
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="ghost" onClick={cancelEdit} className="h-7 px-2 text-xs">
                        Cancel
                      </Button>
                      <Button size="sm" onClick={saveEdit} disabled={savingEdit || !editingText.trim()} className="h-7 px-2 text-xs">
                        Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{linkify(msg.message_text)}</p>
                )}
                {msg.button_label && msg.button_route && (
                  <div className="mt-1.5 text-xs opacity-80 flex items-center gap-1">
                    <Link2 className="h-3 w-3" />
                    {msg.button_label} → {msg.button_route}
                  </div>
                )}
                <p className={cn(
                  "text-[10px] mt-1",
                  msg.sender_role === 'client' ? 'text-muted-foreground' : 'text-primary-foreground/70'
                )}>
                  {new Date(msg.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                  {' · '}
                  {new Date(msg.created_at).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })}
                  {msg.edited_at && <span className="ml-1 italic">(edited)</span>}
                </p>
              </div>
              <MessageReactions
                messageId={msg.id}
                reactions={reactionsByMessage[msg.id] ?? []}
                onToggle={toggleReaction}
                alignEnd={msg.sender_role === 'coach'}
              />
              {idx === lastSeenCoachIdx && clientLastReadAt && (
                <p className="text-[10px] text-muted-foreground mt-1 self-end flex items-center gap-1">
                  <Check className="h-3 w-3" />
                  Seen {new Date(clientLastReadAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
              </div>
              {msg.sender_role === 'client' && (
                <button
                  type="button"
                  onClick={() => startReplyTo(msg)}
                  className="self-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity h-7 w-7 rounded-full bg-muted hover:bg-muted/80 text-muted-foreground flex items-center justify-center shrink-0"
                  aria-label="Reply to message"
                >
                  <Reply className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      <div className="p-3 border-t space-y-2">
          {replyingTo && (
            <ChatQuotedPreview
              message={replyingTo}
              senderLabel={`Replying to ${senderLabelFor(replyingTo.sender_role)}`}
              variant="composer"
              onCancel={() => setReplyingTo(null)}
              onClick={() => scrollToMessage(replyingTo.id)}
            />
          )}
          <Textarea
            ref={textareaRef}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Reply…"
            className="min-h-[44px] max-h-[100px] resize-none text-sm"
            rows={1}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendReply();
              }
            }}
          />
          <div className="flex items-center justify-between">
            <Collapsible open={showActionLink} onOpenChange={setShowActionLink}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="text-xs gap-1 h-7 px-2">
                  <Link2 className="h-3 w-3" />
                  {showActionLink ? 'Hide link' : 'Action link'}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-2 pt-2">
                <Input
                  value={buttonLabel}
                  onChange={(e) => setButtonLabel(e.target.value)}
                  placeholder="Button label"
                  className="text-sm h-8"
                />
                <Select
                  value={buttonRoute}
                  onValueChange={(val) => setButtonRoute(val)}
                >
                  <SelectTrigger className="text-sm h-8">
                    <SelectValue placeholder="Select a page…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="/">Dashboard</SelectItem>
                    <SelectItem value="/tasks">Tasks</SelectItem>
                    <SelectItem value="/sessions">Sessions</SelectItem>
                    <SelectItem value="/practice">Practice</SelectItem>
                    <SelectItem value="/practice?tab=vocabulary">Vocabulary</SelectItem>
                    <SelectItem value="/practice?tab=grammar">Grammar</SelectItem>
                    <SelectItem value="/practice?tab=writing">Writing</SelectItem>
                    <SelectItem value="/practice?tab=audio">Audio</SelectItem>
                    <SelectItem value="/practice?tab=partners">Partners</SelectItem>
                    <SelectItem value="/practice?tab=hub">Practice Hub</SelectItem>
                    <SelectItem value="/growth">Growth</SelectItem>
                    <SelectItem value="/growth?tab=growth">Growth Check-in</SelectItem>
                    <SelectItem value="/growth?tab=reflections">Reflections</SelectItem>
                    <SelectItem value="/growth?tab=goals">Goals</SelectItem>
                    <SelectItem value="/group-booking">Group Booking</SelectItem>
                  </SelectContent>
                </Select>
              </CollapsibleContent>
            </Collapsible>
            <div className="flex items-center gap-1">
              <EmojiPickerButton textareaRef={textareaRef} setValue={setReply} className="h-8 w-8" />
              <Button
                size="sm"
                onClick={handleSendReply}
                disabled={!reply.trim() || sending}
                className="gap-1 h-8"
              >
                <Send className="h-3.5 w-3.5" />
                Send
              </Button>
            </div>
          </div>
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this message?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the message from the conversation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function CoachChatPanel({ open, onClose }: CoachChatPanelProps) {
  const isMobile = useIsMobile();
  const { threads, refetch, markThreadRead, getOrCreateThread } = useCoachThreads();
  const [view, setView] = useState<'list' | 'new' | 'detail'>('list');
  const [selectedThread, setSelectedThread] = useState<CoachThread | null>(null);

  useEffect(() => {
    if (open) setView('list');
  }, [open]);

  const handleSelectThread = (thread: CoachThread) => {
    setSelectedThread(thread);
    setView('detail');
    void markThreadRead(thread.id);
  };

  const handleNewMessage = () => {
    setView('new');
  };

  const handlePickClient = async (clientId: string) => {
    const thread = await getOrCreateThread(clientId);
    if (!thread) return;
    setSelectedThread(thread);
    setView('detail');
    void markThreadRead(thread.id);
  };

  const content = (
    <div className="h-full flex flex-col">
      {view === 'list' && (
        <CoachThreadListView
          threads={threads}
          onSelectThread={handleSelectThread}
          onNewMessage={handleNewMessage}
        />
      )}
      {view === 'new' && (
        <CoachNewMessageView
          onBack={() => setView('list')}
          onPicked={handlePickClient}
        />
      )}
      {view === 'detail' && selectedThread && (
        <CoachThreadDetailView
          thread={selectedThread}
          onBack={() => { setView('list'); setSelectedThread(null); }}
        />
      )}
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={(o) => !o && onClose()}>
        <DrawerContent className="max-h-[85vh]">
          <DrawerHeader className="pb-2">
            <DrawerTitle>Client Messages</DrawerTitle>
            <DrawerDescription>View and reply to client requests</DrawerDescription>
          </DrawerHeader>
          <div className="flex-1 overflow-hidden" style={{ height: '60vh' }}>
            {content}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-[380px] sm:w-[420px] p-0 flex flex-col">
        <SheetHeader className="p-4 pb-2 border-b">
          <SheetTitle>Client Messages</SheetTitle>
          <SheetDescription>View and reply to client requests</SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-hidden">
          {content}
        </div>
      </SheetContent>
    </Sheet>
  );
}
